import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CurrentUserDto,
  LoginInput,
  LoginResponseDto,
  LoginTotpInput,
  SetupInput,
  SystemStatusDto,
  TwoFactorLoginResultDto,
} from '@cpm/shared';
import { api, ApiClientError } from './client.js';

export function useSystemStatus() {
  return useQuery({
    queryKey: ['system', 'status'],
    queryFn: () => api.get<SystemStatusDto>('/api/system/status'),
    staleTime: 60_000,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<CurrentUserDto | null> => {
      try {
        return await api.get<CurrentUserDto>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** True when the login response is the "give me the second factor" challenge. */
export function isTwoFactorChallenge(
  res: LoginResponseDto,
): res is { twoFactorRequired: true } {
  return 'twoFactorRequired' in res && res.twoFactorRequired === true;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<LoginResponseDto>('/api/auth/login', input),
    onSuccess: (res) => {
      // A 2FA challenge is not yet a session — the code step finishes login.
      if (!isTwoFactorChallenge(res)) queryClient.setQueryData(['auth', 'me'], res);
    },
  });
}

/** Login step 2: submit a TOTP or recovery code against the challenge. */
export function useLoginTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginTotpInput) =>
      api.post<TwoFactorLoginResultDto>('/api/auth/login/totp', input),
    onSuccess: (res) => {
      queryClient.setQueryData(['auth', 'me'], res.user);
    },
  });
}

export function useSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetupInput) => api.post<CurrentUserDto>('/api/auth/setup', input),
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'me'], user);
      void queryClient.invalidateQueries({ queryKey: ['system', 'status'] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  });
}
